import LelyUserDefinition from './UserDefinition';
import axios from 'axios';

export interface LelyAuthConfig {
    ssoUrl?: string;
    currentUserUrl?: string;
    accessTokenLocalStorage?: string;
    credentialsUrl?: string;
    validateUserAccess?: (user: LelyUserDefinition) => boolean;
}

export default class LelyAuth {
    private static accessTokenLocalStorage = 'lely_access_token';
    private static currentUserUrl = 'https://hmf-login.next.lely.cloud/api/userinfo';
    private static ssoUrl = 'https://hmf-login.next.lely.cloud/api/web/login/:type';
    private static credentialsUrl = 'https://hmf-login.next.lely.cloud/api/web/login';
    private static validateUserAccess = (user: LelyUserDefinition) => true;

    public static setConfig(config?: LelyAuthConfig): void {
        if (config?.accessTokenLocalStorage) {
            LelyAuth.accessTokenLocalStorage = config?.accessTokenLocalStorage;
        }

        if (config?.ssoUrl) {
            LelyAuth.ssoUrl = config?.ssoUrl;
        }

        if (config?.currentUserUrl) {
            LelyAuth.currentUserUrl = config?.currentUserUrl;
        }

        if (config?.credentialsUrl) {
            LelyAuth.credentialsUrl = config?.credentialsUrl;
        }

        if (config?.validateUserAccess) {
            LelyAuth.validateUserAccess = config?.validateUserAccess;
        }
    }

    public static initSso(type: string): void {
        window.location.href = `${LelyAuth.ssoUrl.replace(':type', type)}?redirect_url=${encodeURIComponent(window.location.href)}`;
    }

    public static isAuthenticated(): boolean {
        return window.localStorage.getItem(LelyAuth.accessTokenLocalStorage) !== null;
    }

    public static async authenticateByCredentials(email: string, password: string): Promise<LelyUserDefinition> {
        if (!LelyAuth.credentialsUrl) {
            throw new Error('No credentialsUrl provided');
        }

        const response = await axios.post<LelyUserDefinition>(LelyAuth.credentialsUrl, {
            email,
            password,
        });

        const user = response.data;

        if (!user.access_token) {
            throw new Error('NO_ACCESS_TOKEN');
        }

        if (!LelyAuth.validateUserAccess(user)) {
            throw new Error('USER_NO_ACCESS');
        }

        window.localStorage.setItem(LelyAuth.accessTokenLocalStorage, user.access_token);
        axios.defaults.headers.common.Authorization = `Bearer ${user.access_token}`;

        return response.data;
    }

    public static async authenticateByToken(token: string): Promise<LelyUserDefinition> {
        LelyAuth.setAccessToken(token);

        const user = await LelyAuth.getCurrentUser();

        if (!LelyAuth.validateUserAccess(user)) {
            LelyAuth.logout();
            throw new Error('USER_NO_ACCESS');
        }

        return user;
    }

    public static async getCurrentUser(): Promise<LelyUserDefinition> {
        return (await axios.get<LelyUserDefinition>(LelyAuth.currentUserUrl)).data;
    }

    private static setAccessToken(token: string): void {
        window.localStorage.setItem(LelyAuth.accessTokenLocalStorage, token);

        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    public static getAccessToken(): string | null {
        if (typeof window === 'undefined') {
            return null;
        }

        return window.localStorage.getItem(LelyAuth.accessTokenLocalStorage);
    }

    public static logout(): void {
        window.localStorage.removeItem(LelyAuth.accessTokenLocalStorage);
    }
}
